import React from 'react';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import Button from '../common/Button';
import HandWave from '../illustrations/HandWave';
import FaceHappy from '../illustrations/FaceHappy';
import FaceSurprised from '../illustrations/FaceSurprised';
import SpeechBubble from '../illustrations/SpeechBubble';
import Stars from '../illustrations/Stars';
import FloatingSlash from '../illustrations/FloatingSlash';
import ScribbleUnderline from '../illustrations/ScribbleUnderline';
import QuestJourney from '../illustrations/QuestJourney';
import {useNavigate} from 'react-router-dom'; 
import { ArrowRight } from 'lucide-react';

const Hero = () => {
  const navigate = useNavigate();
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section 
      id="hero"
      ref={ref} 
      // Reduced padding-top on mobile, increased on desktop
      className="relative min-h-screen flex items-center bg-white pt-20 md:pt-0 px-4 sm:px-6 overflow-hidden"
    >
      {/* Floating illustrations - Hidden on mobile */}
      <FloatingSlash className="hidden md:block absolute top-20 left-20 opacity-30" color="#00FFC6" />
      <FloatingSlash className="hidden md:block absolute top-28 right-32 opacity-30" color="#9CA3AF" />
      <FloatingSlash className="hidden md:block absolute bottom-40 left-32 opacity-20" color="#00FFC6" />
      <FaceHappy className="hidden md:block absolute top-20 right-20 text-black opacity-60" />
      <SpeechBubble className="hidden md:block absolute top-36 left-32 text-black opacity-50" text="✓" />
      <Stars className="hidden md:block absolute bottom-32 right-28 text-black opacity-40" />
      
      <div className={`w-full max-w-7xl mx-auto transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}>
        <div className="flex flex-col lg:flex-row items-center justify-between gap-4 lg:gap-8">
          {/* Text Content - Left Side */}
          <div className="w-full lg:w-1/2 text-center lg:text-left">
            {/* Added margin-top on mobile, reduced on desktop */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-3 sm:mb-4 lg:mb-6 leading-tight mt-4 lg:mt-0">
              Don't make them <br className="hidden sm:block" />
              fill a form. <br className="hidden sm:block" />
              Let them{' '}
              <span className="relative inline-block">
                play it.
                <ScribbleUnderline delay={0.3} color="#FF1493" />
              </span>
            </h1>
            
            {/* Reduced margins for better spacing on mobile */}
            <p className="text-lg sm:text-xl text-gray-700 mb-4 sm:mb-6 lg:mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Fylo transforms your boring data collection into a gamified 
              experience that people will actually enjoy completing.
            </p>

            {/* Buttons - Stack on mobile, row on larger screens */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start mb-4 sm:mb-6 lg:mb-8">
              <Button 
                onClick={() => {
                  navigate('/signup'); 
                }}
                className="px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg w-full sm:w-auto"
              >
                Build your first form
                <ArrowRight/>
              </Button>
            </div>

            {/* Stats - Grid layout for mobile */}
            <div className="grid grid-cols-3 gap-4 sm:gap-6 text-gray-600 max-w-md mx-auto lg:mx-0">
              <div className="text-center lg:text-left">
                <div className="text-xl sm:text-2xl font-bold text-black">94%</div>
                <div className="text-xs sm:text-sm">Completion Rate</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-xl sm:text-2xl font-bold text-black">3x</div>
                <div className="text-xs sm:text-sm">More Engagement</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-xl sm:text-2xl font-bold text-black">2.5x</div>
                <div className="text-xs sm:text-sm">Faster Responses</div>
              </div>
            </div>
          </div>

          {/* Illustration - Right Side - Hidden on mobile, show on lg and up */}
          <div className="w-full lg:w-1/2 hidden lg:flex justify-center lg:justify-end">
            <div className="relative max-w-lg w-full">
              <div className="bg-transparent">
                <QuestJourney />
              </div>
              
              {/* Interactive Form Example */}
              <div className="mt-6 bg-gray-50 rounded-2xl p-6 max-w-md mx-auto lg:mx-0 lg:ml-auto">
                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <h3 className="text-lg font-bold mb-3">What brings you here today?</h3>
                  <div className="space-y-2">
                    <div className="p-3 border-2 border-black rounded-lg hover:bg-black hover:text-white cursor-pointer transition-all duration-300 text-center text-sm">
                      Curious about Fylo?
                    </div>
                    <div className="p-3 border-2 border-gray-300 rounded-lg hover:border-black cursor-pointer transition-all duration-300 text-center text-sm">
                      We are here to change the way you collect data.
                    </div>
                    <div className="p-3 border-2 border-gray-300 rounded-lg hover:border-black cursor-pointer transition-all duration-300 text-center text-sm">
                      ✦ Scroll to learn more! ✦ 
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile-only illustration - Moved further down */}
        <div className="lg:hidden mt-12 bg-gray-50 rounded-2xl p-6 mx-auto max-w-md">
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-red-400"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
              <div className="w-3 h-3 rounded-full bg-green-400"></div>
            </div>
            <h3 className="text-lg font-bold mb-3 text-center">What brings you here today?</h3>
            <div className="space-y-2">
              <div className="p-3 border-2 border-black rounded-lg hover:bg-black hover:text-white cursor-pointer transition-all duration-300 text-center">
                Curious about Fylo?
              </div>
              <div className="p-3 border-2 border-gray-300 rounded-lg hover:border-black cursor-pointer transition-all duration-300 text-center">
                We are here to change the way you collect data.
              </div>
              <div className="p-3 border-2 border-gray-300 rounded-lg hover:border-black cursor-pointer transition-all duration-300 text-center">
                ✦ Scroll to learn more! ✦ 
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom decorative elements - Hidden on mobile */}
      <HandWave className="hidden lg:block absolute bottom-8 left-10 text-black opacity-70" />
      <FaceSurprised className="hidden lg:block absolute bottom-8 right-10 text-black opacity-70" />
    </section>
  );
};

export default Hero;